import {
  Box,
  ButtonBase,
  Card,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { ChevronLeft, ChevronRight } from '@material-ui/icons'
import { FullModal, Spacer } from '@vestaboard/installables'
import React, { useMemo, useState } from 'react'

import { Alert } from '@material-ui/lab'
import { FormattedDateTime } from '../../formatted-date-time'
import { getAckTimestampForDelay } from '../../message-appearance-v2/ackTimestamp'
import { useMessageAppearanceAcksByMessageAppearanceIdsV2 } from '../../message-appearance-v2/hooks/useMessageAppearanceAcksByMessageAppearanceIdsV2'
import { TruncatedId } from '../../truncated-id'
import { useMessageHistory } from '../hooks/useMessageHistory'

interface IMessages {
  boardId: string
  onClose: () => void
}

export const Messages = (props: IMessages) => {
  const [cursors, setCurors] = useState<(string | null)[]>([null])
  const { data, loading, error } = useMessageHistory({ boardId: props.boardId, cursor: cursors[cursors.length - 1] })

  const messageAppearanceIds = useMemo(
    () => data?.listBoardMessageAppearancesV2?.messageAppearances.map(a => a.id) ?? [],
    [data]
  )

  const { data: ackData, loading: ackLoading } = useMessageAppearanceAcksByMessageAppearanceIdsV2(
    messageAppearanceIds,
    { skip: !data || messageAppearanceIds.length === 0 }
  )

  const ackTimestampByAppearanceId = useMemo(() => {
    const map = new Map<string, number>()
    const rows = ackData?.messageAppearanceAcksByMessageAppearanceIdsV2 ?? []
    for (const row of rows) {
      if (row?.messageAppearanceId != null) {
        map.set(row.messageAppearanceId, getAckTimestampForDelay(row))
      }
    }
    return map
  }, [ackData])

  return (
    <FullModal onClose={props.onClose}>
      <Card>
        {error ? (
          <Alert severity='error'>Error Loading Messages</Alert>
        ) : loading || !data ? (
          <CircularProgress />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ minWidth: 320 }}>Appearance / Message</TableCell>
                  <TableCell style={{ width: 180 }}>Created</TableCell>
                  <TableCell>Author</TableCell>
                  <TableCell style={{ width: 100 }}>Acknowledged</TableCell>
                  <TableCell style={{ width: 140 }}>Ack Delay</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.listBoardMessageAppearancesV2.messageAppearances.map(messageAppearance => {
                  const { message } = messageAppearance
                  const ackTimestamp = ackTimestampByAppearanceId.get(messageAppearance.id)
                  const baselineMs = messageAppearance.sentAt ?? messageAppearance.created
                  const acknowledgmentText = ackLoading ? '…' : ackTimestamp != null ? 'true' : 'false'
                  const acknowledgmentDelayText =
                    ackLoading || ackTimestamp == null
                      ? ackLoading
                        ? '…'
                        : '—'
                      : `${Math.round(ackTimestamp - baselineMs)} ms`
                  return (
                    <TableRow key={messageAppearance.id}>
                      <TableCell>
                        <Box display='flex' alignItems='center' flexWrap='wrap'>
                          <TruncatedId
                            value={messageAppearance.id}
                            full
                            path={`/message-appearance/${messageAppearance.id}`}
                          />
                          {message?.id ? (
                            <TruncatedId value={message.id} full path={`/message/${message.id}`} />
                          ) : (
                            <Typography component='span' color='textSecondary'>
                              —
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <FormattedDateTime
                          format='MM/DD/YYYY HH:mm:ss'
                          value={messageAppearance?.created ? +messageAppearance.created : undefined}
                        />
                      </TableCell>
                      <TableCell>{messageAppearance?.attribution}</TableCell>
                      <TableCell>{acknowledgmentText}</TableCell>
                      <TableCell>{acknowledgmentDelayText}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Spacer size='large' />
        <Box display='flex' justifyContent='center' alignItems='center'>
          <ButtonBase
            disabled={cursors.length === 1}
            onClick={() => {
              if (cursors.length > 1) {
                setCurors(cursors.slice(0, cursors.length - 1))
              }
            }}>
            <ChevronLeft />
            <Typography>Previous</Typography>
          </ButtonBase>
          <Box width={14} />
          <ButtonBase
            disabled={!data?.listBoardMessageAppearancesV2?.nextCursor}
            onClick={() => {
              if (data?.listBoardMessageAppearancesV2?.nextCursor) {
                setCurors([...cursors, data?.listBoardMessageAppearancesV2?.nextCursor])
              }
            }}>
            <Typography>Next</Typography>
            <ChevronRight />
          </ButtonBase>
        </Box>
        <Spacer size='large' />
      </Card>
    </FullModal>
  )
}
